import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * Circumference-lines measuring tool.
 *
 * A read-only measuring stack: horizontal lines on the socket, each reporting
 * the circumference (and M-L / A-P width) at its height twice over — `original`
 * ("Scan": the socket BEFORE Circumference Modification was applied) and
 * `modified` ("Mod": the socket as it stands right now). Nothing here deforms
 * geometry; both readings are re-measured whenever the socket changes or a line
 * moves, so the tool stays truthful after any later modification.
 *
 * Which mesh "before" is taken from is decided in the handler and reported in
 * `scanSource` — see the Scan-reference block there.
 *
 * Heights are centimetres relative to the sub-patellar landmark — the same
 * reference the measure-reduction rings use, so a line seeded from an applied
 * reduction lands exactly on its ring.
 */

export const LINE_COLORS = [
  '#ef4444', '#f59e0b', '#10b981', '#3b82f6',
  '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16',
];

export const round1 = (v) => Math.round((v || 0) * 10) / 10;

/** Empty reading — shown as "—" until the mesh has been sliced. */
const NO_READING = { circumference: null, medioLat: null, antPost: null };

export const makeLine = (index, height, color) => ({
  id: index + 1,
  color: color || LINE_COLORS[index % LINE_COLORS.length],
  height: round1(height),
  original: { ...NO_READING },
  modified: { ...NO_READING },
});

const useCircumferenceLinesStore = create(
  devtools((set, get) => ({
    /** @type {Array<ReturnType<typeof makeLine>>} */
    lines: [],
    /** Index of the line the transform gizmo is attached to (-1 = none). */
    currentIndex: 0,

    /**
     * Which reference the `original` (Scan) column is measured against, written
     * by the handler:
     *
     *   'pre-modification' — the socket recorded when the reduction was applied
     *   'scan-baseline'    — the model as loaded (nothing applied, or no snapshot)
     *   'recorded'         — no mesh; per-ring numbers saved at apply time
     *   'none'             — no before on record at all
     */
    scanSource: 'none',

    setLines: (lines) => set({ lines }),
    setCurrentIndex: (currentIndex) => set({ currentIndex }),
    setScanSource: (scanSource) => set({ scanSource }),

    setHeightAt: (index, height) =>
      set({
        lines: get().lines.map((l, i) => (i === index ? { ...l, height: round1(height) } : l)),
      }),

    /**
     * Write freshly measured readings back.
     *
     * `readings` is keyed by line index; a missing key leaves that line alone, so
     * a drag can push just the dragged line's numbers without disturbing the
     * rest. Heights are never touched here — the handler owns those.
     */
    applyReadings: (readings) =>
      set({
        lines: get().lines.map((l, i) => (readings[i] ? { ...l, ...readings[i] } : l)),
      }),

    reset: () => set({ lines: [], currentIndex: 0, scanSource: 'none' }),
  })),
);

export default useCircumferenceLinesStore;
